/**
 * «Скопировать ссылку» — постоянный URL документа (`/processes/:id`), чтобы привязать его
 * в переписке/задаче и передать коллеге. Web — в буфер обмена, native — системный шэр-лист.
 * Открывший ссылку без сессии попадёт на логин и вернётся на документ (AuthGate, `?next=`).
 * Прочитать карточку может только участник маршрута / admin / HR своего филиала
 * (`assertCanView` на бэке) — остальным страница покажет «Нет доступа».
 *
 * С 16.10.2026 это пункт меню «⋯» в футере карточки, а не кнопка в шапке — отсюда хук.
 */
import { useEffect, useState } from 'react'
import { Platform, Share } from 'react-native'
import * as Linking from 'expo-linking'

import { useApp } from '../../i18n'

/** Абсолютная ссылка на документ: на web — от текущего origin, на native — aslife://. */
export const docUrl = (id: string) => Linking.createURL(`/processes/${id}`)

/**
 * copy() + признак «только что скопировано» (2 с) для подписи пункта.
 *
 * URL приходит функцией, а не значением: ссылку на документ собирает `Linking.createURL`, а
 * ему нужен манифест приложения — на рендере каждой карточки он лишний (в тестах его вообще
 * нет). Поэтому адрес считается в момент нажатия. Тем же хуком копируется ссылка на публичную
 * страницу подписания, которую собрал сервер (`/s/<токен>`).
 */
export function useCopyLink(getUrl: () => string) {
  const { l } = useApp()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const copy = async () => {
    const url = getUrl()
    if (Platform.OS !== 'web') {
      await Share.share({ message: url }).catch(() => {})
      return
    }
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      // ponytail: clipboard API нет на http-стендах без TLS — показываем URL для ручного копирования
      window.prompt(l('proc.copyLink'), url)
    }
  }

  return { copy, copied }
}
